/**
 * HinweisePanel (R2-Punkt 5) — „Kommentare & Hinweise" je Gesellschaft.
 *
 * Rendert hinweise[] gruppiert/sortiert nach Schweregrad (WARNUNG → HINWEIS →
 * INFO). Quelle wird dezent ausgewiesen. Leerzustand (leer/undefined):
 * „keine Auffälligkeiten". Erklärung, kein Alarm — keine Ampel.
 */
import type { Hinweis, HinweisSchweregrad } from '../types/snapshot';

/** Anzeige-Reihenfolge nach Schwere (absteigend). */
const ORDER: HinweisSchweregrad[] = ['WARNUNG', 'HINWEIS', 'INFO'];

const BADGE: Record<HinweisSchweregrad, { label: string; cls: string }> = {
  WARNUNG: { label: 'Warnung', cls: 'bg-ampel-red/15 text-ampel-red' },
  HINWEIS: { label: 'Hinweis', cls: 'bg-ampel-yellow/15 text-ampel-yellow' },
  INFO: { label: 'Info', cls: 'bg-ampel-gray/15 text-ampel-gray' },
};

export interface HinweisePanelProps {
  hinweise?: Hinweis[];
}

export function HinweisePanel({ hinweise }: HinweisePanelProps) {
  const list = hinweise ?? [];
  // Stabil nach Schweregrad gruppieren (Reihenfolge innerhalb der Gruppe erhalten).
  const sorted = ORDER.flatMap((grad) => list.filter((h) => h.schweregrad === grad));
  // Einträge mit unbekanntem Schweregrad defensiv anhängen (nichts verschlucken).
  const rest = list.filter((h) => !ORDER.includes(h.schweregrad));
  const ordered = [...sorted, ...rest];

  return (
    <section aria-label="Kommentare & Hinweise" className="space-y-3">
      <h3 className="text-lg font-semibold text-text">Kommentare &amp; Hinweise</h3>
      {ordered.length === 0 ? (
        <p className="text-sm italic text-text-muted">keine Auffälligkeiten</p>
      ) : (
        <ul className="space-y-2">
          {ordered.map((h, i) => {
            const b = BADGE[h.schweregrad] ?? { label: h.schweregrad, cls: 'bg-surface-2 text-text-muted' };
            return (
              <li
                key={`${h.kriterium}-${i}`}
                className="flex flex-col gap-1 rounded-lg border border-border bg-surface p-3 sm:flex-row sm:items-start sm:gap-3"
              >
                <span
                  className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-xs font-medium ${b.cls}`}
                >
                  {b.label}
                </span>
                <span className="flex-1 text-sm text-text">{h.text}</span>
                <span className="shrink-0 text-xs text-text-muted">{h.quelle}</span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
